import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PortalWithState } from 'react-portal';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import CustomerEditModal from '../Customer/CustomerEditModal';
import Button from '../Button/Button';
import Address from '../Address/Address';
import { useHistory } from 'react-router-dom';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import useMode from '../../hooks/useMode';
import { Text, Flex, Box } from 'rebass';
import DataTable from '../Table/DataTable';
import customerColumns from './WaterServiceCustomers.columns';
import assemblyColumns from './WaterServicesAssemblies.columns';
import AssemblyEditModal from '../BackflowAssembly/AssemblyEditModal';
import RequirePerms from '@/components/RequirePerms';
import { useMutation } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';
import { ResultOf } from 'gql.tada';
import {
  makeHazardResultColumns,
  HazardResultRow,
} from '../WaterHazard/hazardSurveyResultColumns';
import { GridTable } from '@/components/Table/GridTable';
import FilePreviews from '../File/FilePreviews';
import WaterServicePhotosEdit from './WaterServicePhotosEdit';
import {
  hazardProtectionStatusOptions,
  hazardProtectionStatusStyle,
  HazardProtectionStatus,
  HazardProtectionStatusPicker,
} from './HazardProtectionStatusPicker';
import StatusChip from '../Status/StatusChip';
import WaterHazardsPicker, {
  WaterHazard,
} from '../WaterHazard/WaterHazardsPicker';
import StatusDot from '../Status/StatusDot';
import HazardAssemblyPicker from './HazardAssemblyPicker';
import useNotify from '@/hooks/useNotify';
import useSessionInfo from '@/hooks/useSessionInfo';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../Modal';
import { createPortal } from 'react-dom';

const REMOVE_WATER_SERVICE_ATTACHMENT = gql(`
  mutation RemoveWaterServiceAttachment($waterServiceId: ID!, $attachmentId: ID!) {
    removeWaterServiceAttachment(waterServiceId: $waterServiceId, attachmentId: $attachmentId)
  }
`);

export enum WaterServiceStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
}

export const GET_WATER_SERVICE_DETAIL = gql(`
  query GetWaterServiceDetail($where: JSON) {
    waterServices(where: $where) {
      id
      waterSupplierId
      siteNumber
      meterNumber
      meterSize
      radioId
      pin
      recycledWaterSite {
        id
        name
      }
      hazardSite {
        id
        name
      }
      type
      sourceType
      status
      tags
      addressId
      address {
        id
        street1
        street2
        city
        state
        zip
        zipExt
      }
      backflowAssemblies {
        id
        created
        model {
          name
          backflowAssemblyMake {
            name
          }
        }
        size
        serialNumber
        testAnniversary
        lastTestDate
        lastTest {
          jobId
          initialResult
          finalResult
        }
        lastNoticeSent {
          created
        }
        status
        testStatus
        location
        type
        hazardType
        hazardLevel
      }
      serviceAddresses(where: { waterServiceId: { ne: null } }, limit: null) {
        id
        customerId
        customerRole
        active
        created
        customer {
          status
          profile {
            name
          }
          accountNumber
        }
        sendNotices
      }
      waterHazardSurveys(orderBy: [{ col: "jobId", dir: DESC }], limit: 1) {
        jobId
        job {
          created
        }
        status
      }
      lastHazardSurveyDate
      nextHazardSurveyDue
      hazardProtectionStatus
      waterServiceHazards {
        waterHazardId
        surveyId
        isProtected
        backflowAssemblyId
        notes
        waterHazard {
          id
          name
          hazardLevel
        }
        backflowAssembly {
          id
          serialNumber
          model {
            name
            backflowAssemblyMake {
              name
            }
          }
        }
      }
      comments {
        id
        profile {
          id
          name
        }
        comment
        created
      }
      attachments {
        id
        created
        contentType
        url
        thumb(size: 500)
      }
    }
  }
`);

export type WaterService = ResultOf<
  typeof GET_WATER_SERVICE_DETAIL
>['waterServices'][number];

export type WaterServiceHazard = WaterService['waterServiceHazards'][number];
export type ServiceAddress = WaterService['serviceAddresses'][number];
export type WaterServiceComment = WaterService['comments'][number];

const ADD_WATER_SERVICE_HAZARD = gql(`
  mutation AddWaterServiceHazard(
    $waterServiceId: ID!
    $waterHazardId: ID!
    $isProtected: HazardAnswer
    $backflowAssemblyId: ID
  ) {
    addWaterServiceHazard(
      waterServiceId: $waterServiceId
      waterHazardId: $waterHazardId
      isProtected: $isProtected
      backflowAssemblyId: $backflowAssemblyId
    ) {
      waterServiceId
      waterHazardId
      isProtected
      backflowAssemblyId
    }
  }
`);

const REMOVE_WATER_SERVICE_HAZARD = gql(`
  mutation RemoveWaterServiceHazard(
    $waterServiceId: ID!
    $waterHazardId: ID!
  ) {
    removeWaterServiceHazard(
      waterServiceId: $waterServiceId
      waterHazardId: $waterHazardId
    )
  }
`);

const UPDATE_WATER_SERVICE_HAZARD = gql(`
  mutation UpdateWaterServiceHazard(
    $waterServiceId: ID!
    $waterHazardId: ID!
    $isProtected: HazardAnswer
    $backflowAssemblyId: ID
    $notes: String
  ) {
    updateWaterServiceHazard(
      waterServiceId: $waterServiceId
      waterHazardId: $waterHazardId
      isProtected: $isProtected
      backflowAssemblyId: $backflowAssemblyId
      notes: $notes
    ) {
      waterServiceId
      waterHazardId
      isProtected
      backflowAssemblyId
      notes
    }
  }
`);

const SET_HAZARD_PROTECTION_STATUS = gql(`
  mutation SetWaterServiceHazardProtectionStatus(
    $waterServiceId: ID!
    $status: HazardProtectionStatus!
  ) {
    setWaterServiceHazardProtectionStatus(
      waterServiceId: $waterServiceId
      status: $status
    ) {
      id
      hazardProtectionStatus
    }
  }
`);

type WaterServiceDetailProps = {
  waterService: WaterService;
  onChange?: () => void;
};

const WaterServiceDetail: React.FC<WaterServiceDetailProps> = ({
  waterService,
  onChange,
}) => {
  const history = useHistory();
  const mode = useMode();
  const { showError, showSuccess } = useNotify();
  const { features, isAdmin } = useSessionInfo();
  const hasWaterHazards = isAdmin || features.includes('WATER_HAZARDS');

  const [removeWaterServiceAttachment] = useMutation<
    { removeWaterServiceAttachment: boolean },
    { waterServiceId: string; attachmentId: string }
  >(REMOVE_WATER_SERVICE_ATTACHMENT);

  const [assembyIds, setAssemblyIds] = useState(
    waterService ? waterService.backflowAssemblies.map((a) => a.id) : []
  );
  const [customerIds, setCustomerIds] = useState(
    waterService.serviceAddresses.map((customer) => customer.customerId)
  );

  const [selectedHazard, setSelectedHazard] = useState<WaterHazard | null>(
    null
  );
  const [addIsProtected, setAddIsProtected] = useState<string>('UNKNOWN');
  const [addAssemblyId, setAddAssemblyId] = useState<string | null>(null);
  const [editingHazard, setEditingHazard] = useState<WaterServiceHazard | null>(
    null
  );
  const [editIsProtected, setEditIsProtected] = useState<string>('UNKNOWN');
  const [editAssemblyId, setEditAssemblyId] = useState<string | null>(null);

  const [overridingStatus, setOverridingStatus] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<
    HazardProtectionStatus | undefined
  >(undefined);

  const [setHazardProtectionStatus, { loading: settingStatus }] = useMutation(
    SET_HAZARD_PROTECTION_STATUS,
    {
      onCompleted: () => {
        showSuccess('Hazard protection status updated');
        setOverridingStatus(false);
        setPendingStatus(undefined);
        onChange?.();
      },
      onError: (err) => showError('Failed to update status', err),
    }
  );

  const [updateWaterServiceHazard] = useMutation(UPDATE_WATER_SERVICE_HAZARD, {
    onCompleted: () => {
      showSuccess('Hazard updated');
      setEditingHazard(null);
      onChange?.();
    },
    onError: (err) => showError('Failed to update hazard', err),
  });

  const [addWaterServiceHazard] = useMutation(ADD_WATER_SERVICE_HAZARD, {
    onCompleted: () => {
      showSuccess('Hazard added');
      setSelectedHazard(null);
      setAddIsProtected('UNKNOWN');
      setAddAssemblyId(null);
      onChange?.();
    },
    onError: (err) => showError('Failed to add hazard', err),
  });

  const [removeWaterServiceHazard] = useMutation(REMOVE_WATER_SERVICE_HAZARD, {
    onCompleted: () => {
      showSuccess('Hazard removed');
      onChange?.();
    },
    onError: (err) => showError('Failed to remove hazard', err),
  });

  useEffect(() => {
    setAssemblyIds(waterService.backflowAssemblies.map((a: any) => a.id));
    setCustomerIds(
      waterService.serviceAddresses.map((customer) => customer.customerId)
    );
  }, [waterService]);

  const customers = useMemo<ServiceAddress[]>(() => {
    const indexed = waterService.serviceAddresses.reduce(
      (indexed, serviceAddress) => {
        indexed[serviceAddress.customerId] = serviceAddress;
        return indexed;
      },
      {} as Record<string, ServiceAddress>
    );

    return Object.keys(indexed)
      .map((key) => indexed[key])
      .sort((a, b) => {
        const aPoints =
          +(a.active ?? false) + +(a.customer?.status === 'ACTIVE');
        const bPoints =
          +(b.active ?? false) + +(b.customer?.status === 'ACTIVE');
        return bPoints - aPoints;
      });
  }, [waterService.serviceAddresses]);

  const assemblies = useMemo(() => {
    return waterService.backflowAssemblies.slice().sort((a, b) => {
      return +(b.status === 'ACTIVE') - +(a.status === 'ACTIVE');
    });
  }, [waterService.backflowAssemblies]);

  return (
    <>
      <Accordion title="Details">
        <AccordionItem label="Service" value={waterService.siteNumber} />
        <AccordionItem
          label="Meter"
          value={
            (waterService.meterSize ? `${waterService.meterSize}" ` : '') +
            waterService.meterNumber
          }
        />
        <AccordionItem label="TRAC" value={waterService.pin} />
        {waterService.recycledWaterSite && (
          <AccordionItem
            kind="link"
            label="Recycled Site"
            value={waterService.recycledWaterSite.name}
            href={`/${mode}/recycled-sites/${waterService.recycledWaterSite.id}`}
          />
        )}
        {waterService.type && (
          <AccordionItem
            label="Property Type"
            value={constantToLabel(waterService.type)}
          />
        )}
        <AccordionItem
          label="Source Type"
          value={constantToLabel(waterService.sourceType)}
        />
        <AccordionItem
          label="Address"
          value={<Address address={waterService.address} />}
        />
        {hasWaterHazards && waterService.hazardSite && (
          <AccordionItem
            kind="link"
            label="Hazard Site"
            value={waterService.hazardSite.name}
            href={`/${mode}/hazard-sites/${waterService.hazardSite.id}`}
          />
        )}
        {hasWaterHazards && waterService.waterHazardSurveys?.[0] && (
          <AccordionItem
            kind="link"
            label="Last Hazard Survey"
            value={
              waterService.waterHazardSurveys[0].job.created != null
                ? new Date(
                    waterService.waterHazardSurveys[0].job.created
                  ).toLocaleDateString()
                : '-'
            }
            href={`/${mode}/hazard-surveys/${waterService.waterHazardSurveys[0].jobId}`}
          />
        )}
        {hasWaterHazards && !waterService.waterHazardSurveys?.[0] && (
          <AccordionItem label="Last Hazard Survey" value={'-'} />
        )}
        {hasWaterHazards && waterService.nextHazardSurveyDue && (
          <AccordionItem
            label="Next Survey Due"
            value={waterService.nextHazardSurveyDue}
          />
        )}
        {hasWaterHazards && (
          <AccordionItem
            label="Hazard Protection"
            value={
              overridingStatus ? (
                <Flex alignItems="center" sx={{ gap: 2 }}>
                  <Box sx={{ minWidth: 180 }}>
                    <HazardProtectionStatusPicker
                      value={pendingStatus}
                      onChange={(val) =>
                        setPendingStatus(
                          val as HazardProtectionStatus | undefined
                        )
                      }
                    />
                  </Box>
                  <Button
                    icon="save"
                    disabled={!pendingStatus || settingStatus}
                    onClick={() => {
                      if (pendingStatus) {
                        setHazardProtectionStatus({
                          variables: {
                            waterServiceId: waterService.id,
                            status: pendingStatus,
                          },
                        });
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    icon="slash-circle"
                    onClick={() => {
                      setOverridingStatus(false);
                      setPendingStatus(undefined);
                    }}
                  >
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Flex alignItems="center" sx={{ gap: 2 }}>
                  <StatusChip
                    status={hazardProtectionStatusStyle(
                      waterService.hazardProtectionStatus
                    )}
                  >
                    {hazardProtectionStatusOptions.find(
                      (o) => o.value === waterService.hazardProtectionStatus
                    )?.label ?? waterService.hazardProtectionStatus}
                  </StatusChip>
                  <RequirePerms waterServices="UPDATE">
                    <Button
                      variant="outline"
                      icon="edit"
                      onClick={() => {
                        setPendingStatus(waterService.hazardProtectionStatus);
                        setOverridingStatus(true);
                      }}
                    >
                      Override
                    </Button>
                  </RequirePerms>
                </Flex>
              )
            }
          />
        )}
      </Accordion>
      <Accordion
        title="Customers"
        icon={
          <RequirePerms customers="CREATE" waterServices="UPDATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button variant="primary" icon="plus" onClick={openPortal}>
                    Customer
                  </Button>
                  {portal(
                    //this From<odal needs to be adjusted!!!
                    <CustomerEditModal
                      action="add"
                      customer={{
                        profile: {
                          contactAddressId: waterService.address.id,
                        },
                      }}
                      waterServiceId={waterService.id}
                      onClose={() => {
                        closePortal();
                        if (onChange) {
                          onChange();
                        }
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        }
      >
        {customers && customers.length ? (
          <DataTable
            columns={customerColumns}
            itemIds={customerIds}
            onRowClick={(item: WaterService['serviceAddresses'][number]) => {
              history.push(`/${mode}/customers/${item.customerId}`);
            }}
            items={customers}
          />
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            There are no customers assigned to this location.
          </Text>
        )}
      </Accordion>
      {hasWaterHazards && (
        <Accordion
          title="Hazards"
          icon={
            <RequirePerms waterServices="UPDATE">
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button variant="primary" icon="plus" onClick={openPortal}>
                      Add Hazard
                    </Button>
                    {portal(
                      <Modal>
                        <ModalHeader title="Add Hazard" />
                        <ModalBody>
                          <Flex
                            p={3}
                            flexDirection="column"
                            sx={{ gap: 3, minWidth: 380 }}
                          >
                            <Box>
                              <Text variant="label" mb={1}>
                                Hazard
                              </Text>
                              <WaterHazardsPicker
                                autoFocus
                                onChange={(hazards) => {
                                  const arr = Array.isArray(hazards)
                                    ? [...hazards]
                                    : [hazards];
                                  setSelectedHazard(
                                    (arr[arr.length - 1] as WaterHazard) ?? null
                                  );
                                }}
                                placeholder="Select a hazard..."
                              />
                            </Box>
                            <HazardAssemblyPicker
                              assemblies={assemblies}
                              isProtected={
                                addIsProtected as 'UNKNOWN' | 'YES' | 'NO'
                              }
                              onIsProtectedChange={setAddIsProtected}
                              assemblyId={addAssemblyId}
                              onAssemblyIdChange={setAddAssemblyId}
                            />
                          </Flex>
                        </ModalBody>
                        <ModalFooter>
                          <Button
                            icon="plus"
                            disabled={!selectedHazard}
                            onClick={() => {
                              if (selectedHazard) {
                                addWaterServiceHazard({
                                  variables: {
                                    waterServiceId: waterService.id,
                                    waterHazardId: selectedHazard.id,
                                    isProtected: addIsProtected as
                                      | 'UNKNOWN'
                                      | 'YES'
                                      | 'NO',
                                    backflowAssemblyId:
                                      addAssemblyId ?? undefined,
                                  },
                                });
                                closePortal();
                              }
                            }}
                          >
                            Add
                          </Button>
                          <Button
                            variant="outline"
                            icon="slash-circle"
                            onClick={() => {
                              setSelectedHazard(null);
                              setAddIsProtected('UNKNOWN');
                              setAddAssemblyId(null);
                              closePortal();
                            }}
                          >
                            Cancel
                          </Button>
                        </ModalFooter>
                      </Modal>
                    )}
                  </>
                )}
              </PortalWithState>
            </RequirePerms>
          }
        >
          {/* Edit Hazard Modal */}
          {editingHazard &&
            createPortal(
              <Modal>
                <ModalHeader
                  title={`Edit: ${editingHazard.waterHazard.name}`}
                />
                <ModalBody>
                  <Flex
                    p={3}
                    flexDirection="column"
                    sx={{ gap: 3, minWidth: 380 }}
                  >
                    <HazardAssemblyPicker
                      assemblies={assemblies}
                      isProtected={editIsProtected as 'UNKNOWN' | 'YES' | 'NO'}
                      onIsProtectedChange={setEditIsProtected}
                      assemblyId={editAssemblyId}
                      onAssemblyIdChange={setEditAssemblyId}
                    />
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  <Button
                    icon="save"
                    onClick={() => {
                      updateWaterServiceHazard({
                        variables: {
                          waterServiceId: waterService.id,
                          waterHazardId: editingHazard.waterHazardId,
                          isProtected: editIsProtected as
                            | 'UNKNOWN'
                            | 'YES'
                            | 'NO',
                          backflowAssemblyId:
                            editIsProtected === 'YES'
                              ? (editAssemblyId ?? null)
                              : null,
                        },
                      });
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    icon="slash-circle"
                    onClick={() => setEditingHazard(null)}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </Modal>,
              document.body
            )}
          {(waterService.waterServiceHazards?.length ?? 0) > 0 ? (
            <GridTable
              columns={[
                {
                  id: 'hazard',
                  header: 'Hazard',
                  accessor: (row) => (
                    <Flex sx={{ gap: 1 }} alignItems="center">
                      <StatusDot
                        status={
                          row.waterHazard.hazardLevel === 'HIGH'
                            ? 'error'
                            : 'warning'
                        }
                      />
                      <Flex flexDirection="column">
                        <Text variant="body2" fontWeight="bold">
                          {row.waterHazard.name}
                        </Text>
                        {row.surveyId ? null : (
                          <Text fontSize={0} color="gray.500">
                            manual
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  ),
                },
                {
                  id: 'protected',
                  header: 'Protected',
                  accessor: (row) => (
                    <Flex alignItems="center" sx={{ gap: 1 }}>
                      <StatusChip
                        status={
                          row.isProtected === 'YES'
                            ? 'success'
                            : row.isProtected === 'NO'
                              ? 'error'
                              : 'default'
                        }
                      >
                        {row.isProtected === 'YES'
                          ? 'Yes'
                          : row.isProtected === 'NO'
                            ? 'No'
                            : 'Unknown'}
                      </StatusChip>
                      {row.backflowAssembly && (
                        <Text fontSize={0} color="gray.500">
                          {[
                            row.backflowAssembly.model?.backflowAssemblyMake
                              ?.name,
                            row.backflowAssembly.model?.name,
                            row.backflowAssembly.serialNumber
                              ? `#${row.backflowAssembly.serialNumber}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        </Text>
                      )}
                    </Flex>
                  ),
                },
                {
                  id: 'actions',
                  header: '',
                  width: 92,
                  accessor: (row) => (
                    <RequirePerms waterServices="UPDATE">
                      <Flex sx={{ gap: 1 }}>
                        <Button
                          variant="outline"
                          icon="edit"
                          title="Edit hazard"
                          onClick={() => {
                            setEditingHazard(
                              row as unknown as WaterServiceHazard
                            );
                            setEditIsProtected(row.isProtected ?? 'UNKNOWN');
                            setEditAssemblyId(row.backflowAssemblyId ?? null);
                          }}
                        />
                        <Button
                          variant="danger"
                          icon="trash"
                          title="Remove hazard"
                          onClick={() => {
                            if (
                              confirm(
                                `Remove "${row.waterHazard.name}" from this water service?`
                              )
                            ) {
                              removeWaterServiceHazard({
                                variables: {
                                  waterServiceId: waterService.id,
                                  waterHazardId: row.waterHazardId,
                                },
                              });
                            }
                          }}
                        />
                      </Flex>
                    </RequirePerms>
                  ),
                },
              ]}
              items={waterService.waterServiceHazards!.map((h) => ({
                ...h,
                id: h.waterHazardId,
              }))}
            />
          ) : (
            <Text variant="body2" color="fieldBorderHover">
              No hazards recorded for this service.
            </Text>
          )}
        </Accordion>
      )}
      <Accordion
        title="Backflow Assemblies"
        icon={
          <RequirePerms assemblies="CREATE" waterServices="UPDATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button variant="primary" icon="plus" onClick={openPortal}>
                    Assembly
                  </Button>
                  {portal(
                    <AssemblyEditModal
                      action="create"
                      assembly={{
                        addressId: waterService.address.id,
                        waterSupplierId:
                          waterService.waterSupplierId ?? undefined,
                        waterServiceId: waterService.id,
                      }}
                      onClose={() => {
                        closePortal();
                        if (onChange) {
                          onChange();
                        }
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        }
      >
        {assemblies && assemblies.length ? (
          <DataTable
            columns={assemblyColumns}
            itemIds={assembyIds}
            items={assemblies}
            onRowClick={(item: WaterService['backflowAssemblies'][number]) => {
              history.push(`/${mode}/assemblies/${item.id}`);
            }}
          />
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            There are no assemblies at this location.
          </Text>
        )}
      </Accordion>
      <Accordion
        title="Images"
        icon={
          <RequirePerms waterServices="UPDATE">
            <WaterServicePhotosEdit
              waterServiceId={waterService.id}
              onPhotosAdded={onChange}
            />
          </RequirePerms>
        }
      >
        {waterService.attachments && waterService.attachments.length > 0 ? (
          <FilePreviews
            files={waterService.attachments}
            onDelete={(file) => {
              if (!confirm('Are you sure you want to delete this photo?')) {
                return;
              }
              removeWaterServiceAttachment({
                variables: {
                  waterServiceId: waterService.id,
                  attachmentId: file.id,
                },
                onCompleted: onChange,
              });
            }}
            mb={2}
          />
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            No images attached to this water service.
          </Text>
        )}
      </Accordion>
    </>
  );
};

WaterServiceDetail.propTypes = {
  waterService: PropTypes.any,
};

export default WaterServiceDetail;
